import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Create api instance that always reads fresh token
  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
    });
    
    instance.interceptors.request.use((config) => {
      const currentToken = localStorage.getItem('token');
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    });
    
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
        return Promise.reject(error);
      }
    );
    
    return instance;
  }, []);

  const fetchUser = useCallback(async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      setToken(storedToken);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = async (accessTokenOrEmail, passwordOrUser) => {
    // Check if this is direct token/user login (from email verification)
    if (typeof passwordOrUser === 'object' && passwordOrUser !== null) {
      const accessToken = accessTokenOrEmail;
      const userData = passwordOrUser;
      localStorage.setItem('token', accessToken);
      setToken(accessToken);
      setUser(userData);
      return userData;
    }
    
    // Standard email/password login
    const email = accessTokenOrEmail;
    const password = passwordOrUser;
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, firstName, lastName, referralCode = null) => {
    const payload = { 
      email, 
      password, 
      first_name: firstName,
      last_name: lastName
    };
    
    // Only include referral_code if provided
    if (referralCode) {
      payload.referral_code = referralCode;
    }
    
    const response = await api.post('/auth/register', payload);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    await fetchUser();
  };

  const updateCredits = (newCredits) => {
    setUser(prev => prev ? { ...prev, credits: newCredits } : null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      logout,
      refreshUser,
      updateCredits,
      api,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};
